/**
 * Co-pilot prompt content, ported from Anvil's `src/state/store.ts`
 * (`COPILOT_GUIDE` + `composeSystemPrompt`). Model-agnostic prose — see
 * TDD §7.3. Lightly neutralised ("Design co-pilot" rather than "Anvil")
 * pending the naming decision (§12 Q9).
 *
 * Composes project + current-section context, plus optional linked screens
 * and active lenses.
 */
import type { Screen, DesignProfile } from '@volley/crucible-ipc-types';
import type { DesignSystemRecord } from '@volley/crucible/core/design-system-record.js';
import { genreLabel } from './lib/profile-options';
import { parseItems } from './lib/section-items';

/**
 * Shared description of the modular-item model so every AI surface (chat,
 * wizard drafting, slug↔details) writes content that drops straight into the
 * card UI. Mirrors the convention in lib/section-items.ts.
 */
export const ITEM_MODEL_GUIDE = `# Modular item model
The Systems, Content, and Art Direction sections are edited as a list of discrete ITEMS, not prose. Each item is written in markdown as:

  ## <Item name>            (a short nominative name — e.g. "Inventory")
  _<one-line description>_   (the "slug")

  <details markdown>         (paragraphs, lists, tables — the buildable spec)

An item may be a GROUP that nests child items one level deep using ### headings (e.g. Content ▸ "Inventory Items" ▸ "Potion"). Content is often grouped; Systems and Art Direction are usually flat. Never repeat the item name as a heading inside its own body.

# The Game Loop leads Systems
The Systems section always begins with a single item named "Game Loop" — the core moment-to-moment loop the whole game hangs off (what players do each round/turn and how it resolves). It is the spine of the design:
- Read it FIRST and keep every other Systems item consistent with it; weight it most when proposing, revising, or building mechanics.
- Each Systems item is BOTH a mechanic AND a system (e.g. "Player Movement", "Whisper Chain") — name the concrete mechanics the loop needs.
- Keep "Game Loop" as the first Systems item, never create a second loop item, and never drop it. If a Systems section has no Game Loop yet, propose one first (named exactly "Game Loop").

# Authored sets vs procedural generators
Decide PER GAME how a category is realised, from the pitch and genre:
- AUTHORED SET (deterministic): the specific instances are designed by hand — enumerate them as child items (### entries). e.g. Content ▸ "Levels" ▸ "Water Temple", "Forest", "Hollowdeep", each with its own slug + details.
- PROCEDURAL (generative): there are no fixed instances — write the parent item as a single generator spec (the piece library, parameters, seeding, constraints) and add NO children. Never fabricate placeholder instances like "Level 1 / Level 2" to fill space.
- HYBRID: children are reusable archetypes/building-blocks and the parent body says how they're assembled.
Choose one and commit to it; do not enumerate instances for a procedural system, and do not describe a generator when the game wants hand-made named content.

# Be specific and in-scope (this matters most)
Name concrete things to build, never abstractions. Foundry hands these to a build agent, so vagueness and padding are bugs.
- SLUG: state the specific thing AND its one distinguishing detail — a count, the key parameter, the rule that makes it different. NOT a restatement of the name. Bad: "The reusable room templates the party solves." Good: "6 room archetypes, each cleared on one TV screen with phone-split inputs."
- BODY: enumerate the actual entities / values / examples the build will create (the specific rooms and their win conditions; the specific items and their numbers), scoped to a first playable version. Concrete nouns and quantities over abstract rules.
- Do NOT invent structure or detail the build doesn't need yet: no generic "common contract" sections, no boilerplate restating how the engine works, no sub-headings unless the slug genuinely implies parts. If something is truly shared across items, state it once, briefly, in the section's first item — don't repeat it per item.
- Prefer 3–6 tight, concrete lines over a page of generic prose. When unsure whether a detail earns its place, leave it out.

# Inline references: #tag and @Mention
Design prose supports two inline reference tokens (they render as clickable links in the editor):
- \`#tag\` marks a THEME/CATEGORY (e.g. \`#fire\`, \`#timing\`) — clicking shows everything carrying that tag. An item's own tags live on a standalone \`Tags: fire, hazard\` line in its body.
- \`@Mention\` is a DIRECT reference to another design entry by name, with spaces removed — \`@FirePit\` for "Fire Pit", \`@RollingLog\` for "Rolling Log". Use it whenever a body references a specific existing item/screen/level, so docs stay navigable — e.g. "spawns beside the @FirePit whenever #fire hazards are active."
Only @-mention entries that actually exist in the design (or ones you are creating in the same response); never invent referents.

# Record tunable values softly
When an item has concrete numeric values (heal amounts, damage, jump height, timers, counts, costs), write them as plain labelled lines in the item body — e.g. \`Heals: 40 HP\`, \`Jump height: 8 m\`, \`Cooldown: 3 s\`. These are the soft, human-readable source of intent the build agent reads (there is no separate tunables file). Keep one line per value so they're easy to scan and edit.
- When a change sets a NEW value, update that line in place (don't add a second).
- For a RELATIVE change ("twice as high", "+50% damage"), compute the new value from the current labelled value when it's present (e.g. \`Jump height: 4 m\` → \`Jump height: 8 m\`) and write the result. If no current value is recorded, state the relative change plainly (e.g. "Jump height: doubled") rather than inventing a precise number.`;

export const COPILOT_GUIDE = `You are the Foundry Design Co-Designer, helping a designer at Weekend (formerly Volley) author a Game Design Document (GDD) or Product Requirements Document (PRD).

Constraints:
- Weekend GDDs explain HOW the game works for engineers; PRDs explain WHAT and WHY for product. Keep these audiences separate when proposing content.
- Weekend games are TV+phone multiplayer, voice-driven, host-led.
- The Volley Game Framework (VGF) uses a Session/Phase/Reducer model, with separate display, controller, server, and shared layers. Set design intent without prescribing code.
- The user's goal is zero-ambiguity design specs that can be handed to Foundry for an AI build agent to implement.
- When you propose section content, output ONLY markdown — no preamble, no commentary — so it can be applied in one click. For the modular item sections the app MERGES your items into the open section by name: to ADD or CHANGE an item, return just that item (the user's other items are preserved — never re-output or drop them). To ADD entries to an existing group (e.g. add questions to a trivia category, levels to a world), return that group's \`## Group name\` heading followed by ONLY the new \`### Entry\` children — the app merges children by name and keeps the existing entries, so never re-list the ones already there. Emit any item or entry as a heading in this convention, never as a bare list or prose — content that isn't headed items can't be merged. Only return a section's complete contents when you truly intend to replace the whole thing. If you're genuinely just chatting (not proposing content to apply), respond conversationally.

${ITEM_MODEL_GUIDE}`;

/** Render a game profile as a prompt block so the AI shares the design's terms. */
export function profileContextBlock(profile: DesignProfile | undefined): string {
  if (!profile) return '';
  const lines: string[] = [];
  if (profile.genre) lines.push(`Genre: ${genreLabel(profile.genre)}`);
  if (profile.players) {
    lines.push(
      profile.players.mode === 'single'
        ? 'Players: single-player'
        : `Players: multiplayer (up to ${profile.players.max})`,
    );
  }
  if (profile.inputStyles?.length) lines.push(`Input style: ${profile.inputStyles.join(', ')}`);
  if (profile.artStyle) lines.push(`Art style: ${profile.artStyle}`);
  return lines.length ? `\n\n# Game profile\n${lines.join('\n')}` : '';
}

/**
 * crucible#2968 — render the create-time design agent's persisted total
 * identity (`.foundry/persistent/design-system.json`) as a prompt block, so
 * the Wizard's drafted content stays consistent with the SAME palette/
 * art-style/mood the real build and Art tab already use, instead of the
 * model re-deriving a different one from the pitch alone. `null`/`undefined`
 * (no design agent ran, or the read failed) ⇒ empty string — byte-identical
 * to the pitch-only drafting this replaces.
 */
export function designSystemContextBlock(record: DesignSystemRecord | null | undefined): string {
  if (!record) return '';
  const ds = record.designSystem;
  const lines: string[] = [];
  if (ds.headlineWow) lines.push(`Headline wow: ${ds.headlineWow}`);
  if (ds.mood.length) lines.push(`Mood: ${ds.mood.join(', ')}`);
  const roles = ds.palette?.roles;
  if (roles) {
    lines.push(
      `Palette: background ${roles.background}, surface ${roles.surface}, accent ${roles.accent}, text ${roles.text}`,
    );
  }
  if (ds.artStyle?.styleKey) lines.push(`Art style key: ${ds.artStyle.styleKey}`);
  if (ds.artStyle?.medium) lines.push(`Art style medium: ${ds.artStyle.medium}`);
  if (!lines.length) return '';
  return (
    '\n\n# Locked design system (already generated for this game at create time)\n' +
    'This game already has a locked visual/tonal identity from the design agent. Stay ' +
    'consistent with it — do NOT invent a different palette, art style, or mood.\n' +
    lines.join('\n')
  );
}

export interface ComposeSystemPromptArgs {
  projectName: string | null;
  projectType: string | null;
  sectionTitle: string | null;
  sectionDoc: string | null;
  sectionBody: string;
  /** Whether the open section uses the modular item editor — when true the
   *  Co-Designer is told to make surgical, merge-by-name item edits. */
  sectionIsItems?: boolean;
  /** Linked screens + active lenses folded into the prompt. */
  linkedScreens?: Screen[];
  lensBodies?: { name: string; description: string; body: string }[];
  /** Selected Weekend design references (folder-driven) folded into the prompt
   *  as background guidance the Co-Designer can draw on. */
  referenceBodies?: { title: string; body: string }[];
}

export function composeSystemPrompt(args: ComposeSystemPromptArgs): string {
  const parts: string[] = [COPILOT_GUIDE];

  if (args.projectName) {
    parts.push(
      `\n# Project context\nProject: ${args.projectName}${
        args.projectType ? ` (${args.projectType.toUpperCase()})` : ''
      }`,
    );
  }

  if (args.sectionTitle) {
    const header = `\n# Current section\n[${args.sectionDoc?.toUpperCase() ?? ''}] ${args.sectionTitle}`;
    if (args.sectionIsItems) {
      const { items } = parseItems(args.sectionBody);
      const inventory = items.length
        ? items
            .map((it) => {
              const kids = it.children.length
                ? ` (entries: ${it.children.map((c) => c.name).join(', ')})`
                : '';
              return `- ${it.name}${it.slug ? ` — ${it.slug}` : ''}${kids}`;
            })
            .join('\n')
        : '(no items yet)';
      parts.push(
        `${header}\n\nThis section is edited as modular ITEMS (cards). Current items:\n${inventory}\n\nTo edit it, return ONLY the item(s) you add or change, in the \`## Name\` + italic-slug convention — reuse an item's EXACT name to update it in place, or a new name to add one. To add entries (e.g. questions) under an existing group, re-emit that group's \`## Name\` with ONLY the new \`### Entry\` children. The app merges by name and keeps every other item (and a group's existing entries), so never re-output unchanged items or restate the section title. Anything you want applied must be a \`##\`/\`###\` heading — a bare list or prose can't be merged. Full current markdown for reference:\n---\n${args.sectionBody}\n---`,
      );
    } else {
      parts.push(`${header}\n\n---\n${args.sectionBody}\n---`);
    }
  }

  const linkedScreens = args.linkedScreens ?? [];
  if (linkedScreens.length > 0) {
    parts.push(
      `\n# Linked screens\nThis section references the following screen blockouts. Treat them as the canonical layout reference when discussing UI.`,
    );
    for (const sc of linkedScreens) {
      const zoneList = sc.zones
        .map(
          (z) =>
            `  - ${z.label || '(unnamed)'} [${z.kind}] @ (${Math.round(z.x)},${Math.round(
              z.y,
            )}) ${Math.round(z.width)}×${Math.round(z.height)}${z.notes ? ` — ${z.notes}` : ''}`,
        )
        .join('\n');
      parts.push(
        `\n## ${sc.name} (${sc.surface.toUpperCase()})\n${
          sc.description ? sc.description + '\n' : ''
        }Zones:\n${zoneList || '  (no zones yet)'}`,
      );
    }
  }

  const lensBodies = args.lensBodies ?? [];
  if (lensBodies.length > 0) {
    parts.push(
      `\n# Active lenses\nApply these perspectives when evaluating or proposing content. Each lens describes a specialist viewpoint.`,
    );
    for (const lens of lensBodies) {
      parts.push(`\n## Lens: ${lens.name}\n${lens.description}\n\n${lens.body}`);
    }
  }

  const referenceBodies = args.referenceBodies ?? [];
  if (referenceBodies.length > 0) {
    parts.push(
      `\n# Reference material\nThe designer has attached the following Weekend design references. Treat them as authoritative background guidance — apply their principles and standards when evaluating or proposing content.`,
    );
    for (const ref of referenceBodies) {
      parts.push(`\n## Reference: ${ref.title}\n${ref.body}`);
    }
  }

  return parts.join('\n');
}

/** Screen wizard guide — generates wireframe screen blockouts from the design
 *  (ported from Anvil's ScreenWizard). */
export const SCREEN_WIZARD_GUIDE = `You are the Foundry Screen Wizard. You produce wireframe-level screen blockouts (zone rectangles labeled with kind and purpose) for a Weekend (TV+phone multiplayer, voice-driven, host-led) game. The blockouts will be handed to a coding agent (Foundry) that builds the actual UI.

Constraints:
- TV surface = 1920×1080 landscape. Phone surface = 390×844 portrait. All x/y/width/height are in surface coordinates.
- Snap all coordinates to a 20px grid.
- Zone kinds: video, score, timer, list, button, text, host, controller-input, art, other. Pick the closest. Use "host" specifically for the AI host avatar/video, "controller-input" for tap/swipe targets on the phone.
- Each zone needs: label (short and concrete: "Player Score Total", "Host Avatar", "Submit Vote"), kind, position, size, optional notes describing behaviour or content source.
- Aim for 4-10 zones per screen. Don't over-decompose.
- Prefer balanced layouts. Center the host on TV, place score zones in a HUD strip, controller buttons near the bottom of phone.
- Always output strictly valid JSON. No prose preamble, no fenced wrappers.`;

export const SCREEN_WIZARD_TASK = `Task: From the design above, identify every distinct screen the game needs and lay out wireframe zones for each.

Output strict JSON: { "screens": [ { "name": "...", "surface": "tv"|"phone", "description": "...", "zones": [ { "label": "...", "kind": "...", "x": 0, "y": 0, "width": 0, "height": 0, "notes": "..." } ] } ] }

For every TV screen, also include the matching phone controller screen if the game expects player input at that moment. Order screens chronologically through the core loop.`;

/** Wizard guide, ported from Anvil's WizardDialog. Bootstraps a new design
 *  from a pitch (TDD §7.2 "Section drafting (wizard)"). */
export const WIZARD_GUIDE = `You are the Foundry Design Wizard. The designer is starting a new game design document for a Weekend (TV+phone multiplayer, voice-driven, host-led) game.

Constraints:
- Weekend GDDs explain HOW the game works for engineers; PRDs explain WHAT and WHY for product. Keep audiences distinct.
- VGF (Volley Game Framework) uses Session/Phase/Reducer with display, controller, server, shared layers. Set design intent — don't prescribe code.
- The end goal is a zero-ambiguity spec that Foundry's AI build agent can implement.
- Always output strictly valid JSON when asked. No prose preamble, no trailing commentary, no fenced code wrappers.

Platform reality — DO NOT invent scope beyond this unless the designer's pitch explicitly says otherwise:
- Weekend games are LIVING-ROOM SHARED-DEVICE experiences. The TV is the shared display, phones are the controllers. Multiplayer just means everyone in the room is on their phones with the same TV.
- No in-app purchases, microtransactions, battle passes, or monetization mechanics.
- No user-generated content unless the pitch specifically calls for it.
- No accounts/profiles/save-files — sessions are room-based and ephemeral by default.
- DO consider: localization, accessibility (voice + on-screen captions), TV-safe-area, network resilience, AI cost budgets, host-AI personality consistency.`;

/**
 * Per-section drafting guidance for the wizard, keyed by the section's id
 * suffix (so it matches regardless of any doc prefix). Art Direction and
 * Content are critical to the Foundry build process, so they get explicit,
 * opinionated guidance to produce documentation a build agent can act on.
 */
const WIZARD_SECTION_GUIDANCE: Record<string, string> = {
  overview:
    "Summarise the game: the core loop, the player fantasy, and why it's fun as a living-room TV+phone party game. A few tight paragraphs — no marketing fluff.",
  systems:
    'START with the "Game Loop" item (named exactly "Game Loop") — the core moment-to-moment loop: round/turn structure, what players do each turn, and how it resolves. Then specify the supporting mechanics as further items (each is both a mechanic and a system), precisely enough that an engineer could build them without guessing: scoring, win/lose conditions, the actions players take, and how phases transition. Call out the important edge cases inline.',
  'art-direction':
    'Describe the visual style so an artist or art-generation agent can produce consistent assets: overall mood/tone, colour palette, key references/inspirations, typography, UI feel, and the character of motion/animation. Be concrete and opinionated — this is the art style description Foundry will follow.',
  content:
    'List the concrete in-game content Foundry must create — levels/rounds, entities, characters, props, prompts, question sets, and other in-game objects. Describe each item briefly but specifically, in a way a build agent can turn directly into assets/data. Prefer a structured markdown list grouped by type.' +
    ' LEVEL VISION (if the game has levels/stages/boards at all): state it explicitly — how many, what SHAPE each is (side-scroll track / top-down floor plan / data set such as boards or loot tables — a game can mix shapes), how they are named, what escalates between them, and how they unlock (linear, gated, sets, open-pick). The Design tab\'s Levels surface reconciles against this enumeration and generates the missing ones, so name levels individually when the design authors them (never "Level 1..10" placeholders for procedural sets — describe the generator instead).',
};

/** Resolve drafting guidance for a section id (matches the id's slug suffix). */
export function wizardSectionGuidance(sectionId: string): string {
  const slug = sectionId.replace(/^(?:gdd|prd)-/, '').replace(/^\d+-/, '');
  return (
    WIZARD_SECTION_GUIDANCE[slug] ??
    'Draft this section faithfully from the pitch and the designer answers. Use Open Questions for anything genuinely unresolved rather than inventing detail.'
  );
}

/** Hammer change-impact guide, ported from Anvil's HammerDialog. */
export const HAMMER_GUIDE = `You are the Foundry Hammer. The designer wants to change something in their existing GDD/PRD and needs to understand butterfly effects across topologically separate areas of the design.

Constraints:
- Keep GDD vs PRD audiences distinct.
- Weekend games = TV+phone multiplayer, voice-driven, host-led. VGF Session/Phase/Reducer model. Set design intent — don't prescribe code.
- Treat the dependency graph as authoritative for known relationships, but propose new edges if you discover undocumented dependencies through this change.
- Always output strictly valid JSON when asked. No prose preamble, no fenced wrappers.`;

/** Stage-1 plan instructions appended after HAMMER_GUIDE (sections-only port). */
export const HAMMER_STAGE1_INSTRUCTIONS = `This is STAGE 1: plan the changes. Do NOT write full section bodies yet — that happens in stage 2.

Output strict JSON only. Keep every string short (≤500 chars) — no fenced code, no table pipes.

{
  "summary": "one-paragraph description of the change and its scope",
  "sections": [
    {
      "sectionId": "<exact id from the project>",
      "diff": "1-3 sentence summary of what will change and why",
      "rationale": "≤2 sentences on which other parts of the design force this change"
    }
  ]
}

Only include sections that actually need to change.`;

export const HAMMER_CONFLICT_INSTRUCTIONS = `This is the PRE-SYNC CONTRADICTION CHECK. The designer edited several sections at once and they are all treated as sources of truth. Before anything is propagated, find places where two or more of these edited sections DIRECTLY CONTRADICT each other on the SAME concrete design fact — e.g. player count, core-loop length/structure, scoring rules, platform/surface assumptions, or phase names.

Only report a conflict when the sections make incompatible claims about the same fact (e.g. "8 players" vs "6 players"). Do NOT report mere differences in topic, emphasis, or wording, and do NOT invent facts that are not stated in the sections.

Output strict JSON only. Keep every string short (≤300 chars) — no fenced code, no table pipes. Use the EXACT section id shown in each "### <id> — <title>" heading.

{
  "conflicts": [
    {
      "topic": "short label for the disputed fact, e.g. Player count",
      "claims": [
        { "sectionId": "<exact id>", "claim": "what this section asserts about the fact" }
      ],
      "suggestion": "optional one-line reconciliation"
    }
  ]
}

Each conflict's "claims" must list at least two different sections. If the edited sections are consistent, return {"conflicts": []}.`;

/** Adversarial review persona levels 1–5, ported verbatim from Anvil's
 *  ReviewDialog (TDD §7.2 "port verbatim"). */
export const ADVERSARIAL_LEVELS: {
  level: number;
  label: string;
  description: string;
  persona: string;
}[] = [
  {
    level: 1,
    label: 'Collegial',
    description: "Gentle nudges. Look for what's missing rather than what's broken.",
    persona:
      'You are a supportive senior designer. Ask gentle, exploratory questions that help the designer notice gaps without feeling attacked. Lead with curiosity.',
  },
  {
    level: 2,
    label: 'Engaged',
    description: 'Curious peer who pushes for specifics.',
    persona:
      'You are a curious peer designer. Ask specific questions that probe for concrete answers. Treat the design as plausible but under-specified.',
  },
  {
    level: 3,
    label: 'Pointed',
    description: 'Skeptical lead who wants this game to ship well.',
    persona:
      "You are a skeptical design lead doing a pre-greenlight review. Surface real risks. Don't soften — but stay constructive. Each question targets a load-bearing assumption.",
  },
  {
    level: 4,
    label: 'Adversarial',
    description: 'Hostile principal — assume the design is wrong until proven.',
    persona:
      'You are an adversarial principal designer. Assume every claim is wrong until proven. Hunt for hand-waving, magic, untested assumptions, scope creep, and contradictions across sections. Be sharp, specific, and unimpressed by vibes.',
  },
  {
    level: 5,
    label: 'Brutal',
    description: 'Worst-case reviewer. No mercy. No politeness budget.',
    persona:
      'You are the worst-case reviewer this designer will ever face — a hostile playtester, a cost-conscious exec, and a cynical engineer rolled into one. Be brutal but specific. Mock vagueness. Demand numbers, mechanisms, and failure modes. Surface anything that would die in playtest, kill review, or torch the budget.',
  },
];

export function reviewLevelMeta(level: number) {
  return ADVERSARIAL_LEVELS.find((l) => l.level === level) ?? ADVERSARIAL_LEVELS[2];
}

export const REVIEW_GUIDE = `You are the Foundry Design Reviewer. The designer wants a critical pass on their Weekend (TV+phone multiplayer, voice-driven, host-led) GDD/PRD.

Constraints:
- Read the design as written. Don't invent details that aren't there — that's exactly what a review should surface as a question.
- Weekend reality: living-room shared device. No IAP, no microtransactions, no UGC unless the design explicitly says so. Voice-AI host. Concerns that DO apply: localization, accessibility, TV-safe-area, voice failure modes, AI cost budgets, host personality consistency, host-AI latency, controller resilience, room dynamics.
- Be specific. A useful question names the section, names the assumption, and asks something a designer can actually answer in 1-3 sentences.
- Severity: "high" = blocking risk if not answered (kills the game, breaks the experience, or torches budget); "medium" = real risk that affects shape of the build; "low" = open question or polish.
- Always output strictly valid JSON. No prose preamble, no fenced wrappers.`;

// ---------------------------------------------------------------------------
// Modular-item AI (bi-directional slug ↔ details). The Design tab's item-mode
// lets a designer keep a one-line "slug" description and a collapsible details
// body in sync: editing the slug can regenerate the details, and editing the
// details can refresh the slug. Each proposal is reviewed as a diff before it
// is applied (see ItemProposalDialog). Output uses the structured-markdown
// `## LABEL` convention (parseSections) so code fences / tables survive.
// ---------------------------------------------------------------------------

export interface ItemAiContext {
  projectName: string | null;
  /** The section this item lives in, e.g. "Systems" or "Content". */
  sectionTitle: string;
  /** The parent group's name when this is a child item (else null). */
  groupName?: string | null;
  itemName: string;
  /** Current one-line description. */
  slug: string;
  /** Current details body markdown. */
  body: string;
}

function itemContextBlock(ctx: ItemAiContext): string {
  const where = ctx.groupName
    ? `${ctx.sectionTitle} ▸ ${ctx.groupName} ▸ ${ctx.itemName}`
    : `${ctx.sectionTitle} ▸ ${ctx.itemName}`;
  return [
    ctx.projectName ? `Project: ${ctx.projectName}` : '',
    `Location in the design: ${where}`,
    `Item name: ${ctx.itemName}`,
    ctx.slug ? `One-line description: ${ctx.slug}` : '',
  ]
    .filter(Boolean)
    .join('\n');
}

/** Slug → details: rewrite the item's details body to fit its name + one-line
 *  description. Returns a `## BODY` payload (parse with parseSections). */
export function itemDetailsFromSlugPrompt(ctx: ItemAiContext): { system: string; user: string } {
  const user = `${itemContextBlock(ctx)}

${ctx.body.trim() ? `Existing details (revise to fit the description above):\n---\n${ctx.body}\n---` : 'There are no details yet — write them from scratch.'}

Write the DETAILS body for this single item, consistent with its name and slug. Enumerate the specific things to build (the concrete entities, values, and examples), scoped to a first playable version — follow the "Be specific and in-scope" rules above. Keep it tight: roughly 3–6 lines or a short list. Do NOT add generic framing, engine boilerplate, "common contract" sections, or sub-headings the slug doesn't imply, and do not restate the name. Match the brevity of the existing details when revising — only add what's genuinely missing.

IMPORTANT: if the slug describes a SET of named things (levels, rooms, items, enemies…), the individual instances belong as separate ENTRIES, not here. In that case keep this body to the shared framing/rules only (1–3 lines) and do NOT list the instances — they are added as their own nested entries.

Output format — STRICT, plain markdown, NOT JSON, no fences wrapping the whole thing. Exactly one section:

## BODY
<the item details as markdown>

Output nothing before or after the BODY section.`;
  return { system: COPILOT_GUIDE, user };
}

/** Details → slug: distil a single-line description from the item's details.
 *  Returns a `## SLUG` payload (parse with parseSections; take the first line). */
export function itemSlugFromDetailsPrompt(ctx: ItemAiContext): { system: string; user: string } {
  const user = `${itemContextBlock(ctx)}

Details body:
---
${ctx.body || '(empty)'}
---

Write a single-line description (the "slug"): the specific thing plus its one distinguishing detail — a count, the key parameter, or the rule that makes it different — NOT a restatement of the name. Plain text, no markdown, no surrounding quotes, max ~120 characters.

Output format — STRICT. Exactly one section:

## SLUG
<one line>

Output nothing before or after the SLUG section.`;
  return { system: COPILOT_GUIDE, user };
}

/** Intelligent add: from just a name (and optional location), draft both the
 *  one-line description and the details body. Returns `## SLUG` + `## BODY`. */
export function newItemPrompt(ctx: ItemAiContext): { system: string; user: string } {
  const user = `${itemContextBlock(ctx)}

Draft this new item: a one-line description and a details body that fit its name and where it sits in the design. Follow the "Be specific and in-scope" rules — name the concrete things to build, keep the body tight (roughly 3–6 lines), and add no generic framing or boilerplate.

Output format — STRICT, plain markdown, NOT JSON. Exactly these two sections, in order:

## SLUG
<one line — plain text, no markdown, no quotes>

## BODY
<the item details as markdown — do not repeat the name as a heading>

Output nothing before SLUG or after BODY.`;
  return { system: COPILOT_GUIDE, user };
}

/** Revise: apply a freeform designer instruction to a single item, rewriting its
 *  one-line description, its details, AND (for a top-level item) its sub-items.
 *  Returns a `## SLUG` + `## BODY` payload, plus `## ENTRIES` (a list of `##`
 *  sub-items) when sub-items are in play — parse SLUG/BODY/ENTRIES with
 *  parseSections, then the entries with parseItems. The label-based format is
 *  far more robust than asking the model to echo the item's exact heading. */
export function reviseItemPrompt(
  ctx: ItemAiContext,
  instruction: string,
  opts: { currentItemMarkdown: string; allowChildren: boolean },
): { system: string; user: string } {
  const entriesRule = opts.allowChildren
    ? `

This item can have SUB-ITEMS (e.g. the questions in a category, the levels in a world). If the instruction adds, changes, or removes sub-items — "add 20 questions with acceptable answers", "make them harder", "drop the easy ones" — return a third \`## ENTRIES\` section listing the sub-items the item should have AFTER the change: re-output every sub-item you keep, add the requested new ones, and omit any you remove. Each sub-item is its own \`## <name>\` block with a slug + details. If the instruction does NOT touch sub-items, OMIT the \`## ENTRIES\` section entirely and the existing sub-items are kept untouched. Keep the item's own BODY to the shared framing — don't duplicate the sub-items there.

DO THE WORK ON THIS ITEM. The designer deliberately chose "${ctx.itemName}". If they ask to add the sub-items here, ACTUALLY WRITE THEM in the ENTRIES section — produce the full content they asked for (e.g. all 20 questions, each with its acceptable-answer list). Do NOT refuse, defer, or substitute a prose BODY note saying the content "belongs" or "lives" in another item or that you'll author it elsewhere. You may ALSO mention a better home as a brief aside, but only after you have first delivered the requested entries here.`
    : '';
  const entriesShape = opts.allowChildren
    ? `

## ENTRIES
## <Sub-item name>
_<one-line slug>_

<sub-item details>

## <Next sub-item name>
_<slug>_

<details…>`
    : '';
  const user = `${itemContextBlock(ctx)}

Current item (markdown — the WHOLE item, including any sub-items):
---
${opts.currentItemMarkdown.trim() || '(empty)'}
---

Designer's revision instruction: ${instruction}

Apply the instruction to THIS item ("${ctx.itemName}"). Keep its name and overall scope — do not turn it into a different item. Rewrite its one-line description (SLUG) and its details (BODY) so they stay consistent with each other and with the instruction; if the instruction only touches one, return both anyway and leave the other essentially as-is. Follow the "Be specific and in-scope" rules: name concrete things to build, keep bodies tight (~3–6 lines), no generic framing or boilerplate, and don't restate the name.${entriesRule}

Output format — STRICT, plain markdown, NOT JSON, no fences wrapping the whole thing. Use these sections, each introduced by a level-2 heading:

## SLUG
<one line — plain text, no markdown, no quotes>

## BODY
<the item details as markdown — do not repeat the name as a heading>${entriesShape}

Output nothing before SLUG${opts.allowChildren ? '' : ' or after BODY'}.`;
  return { system: COPILOT_GUIDE, user };
}

/** Entries: propose the named child items that belong UNDER a heading (e.g. the
 *  specific levels/rooms/items in a set). Returns an item list (parse with
 *  parseItems) — each becomes a nested entry card. */
export function itemEntriesPrompt(ctx: ItemAiContext): { system: string; user: string } {
  const existing = ctx.body.trim();
  const user = `${itemContextBlock(ctx)}

${existing ? `Current heading details:\n---\n${existing}\n---` : 'The heading has no details yet.'}

Two tasks:
1. ENTRIES — the specific named instances that belong under this heading (the actual levels, rooms, items, enemies…), NOT abstract sub-topics like "common rules" or "shared state". Each is a real thing the build will create, with its own one-line slug and a tight details body. Derive them from the name, slug, and details above; only what's in scope for a first playable version (roughly 3–8).
2. FRAMING — rewrite the heading's own details to keep ONLY what is genuinely shared across all entries (the common rule / one-liner). REMOVE every per-instance detail that now lives in an entry — do not list, bullet, or restate the entries in the framing (avoid duplicating them). If nothing is shared, leave FRAMING empty.

Output format — STRICT, plain markdown, NOT JSON. Exactly two sections in this order:

## FRAMING
<shared framing only — may be empty>

## ENTRIES
## <Entry name>
_<one-line slug>_

<entry details>

## <Next entry name>
_<one-line slug>_

<entry details>

Output nothing before FRAMING or after the last entry.`;
  return { system: COPILOT_GUIDE, user };
}
